// ─────────────────────────────────────────────
//  AccessDenied — the "this page isn't yours" screen, with a way out.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  Setup, Daily Assignments and Schedule each early-returned a bare line of red
//  text for anyone without the role — no topbar, no nav menu, no sign out. A
//  clinician who reached one (from a bookmark, a shared link, or a mistap) had
//  nothing on screen to press: the browser's back button was the only exit, and
//  on the installed mobile app there isn't one.
//
//  So the denial keeps the chrome. Same topbar as the page it stands in for, so
//  the nav menu and the account sign-out are exactly where they were.
//
//  The ink is `surface.errorInk` (#991b1b, 7.60:1 on the page ground), NOT
//  `accent.error` (#dc2626), which measures 4.14:1 here and fails AA for normal
//  text — the trap tokens.js documents at length. All three copies used it.
// ─────────────────────────────────────────────
import React from "react";
import { useNavigate } from "react-router-dom";
import NavMenu from "./NavMenu";
import SignOutButton from "./SignOutButton";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;

export default function AccessDenied({ title = "This page isn't yours to open", body }) {
  const navigate = useNavigate();

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <NavMenu />
        <SignOutButton />
      </div>
      <main style={s.main}>
        <div style={s.card}>
          <h1 style={s.title}>{title}</h1>
          <p style={s.body}>{body}</p>
          <button type="button" style={s.cta} onClick={() => navigate("/home")}>
            Back to your dashboard
          </button>
        </div>
      </main>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: color.surface.base,
    color: color.text.primary,
    fontFamily: font.family,
    display: "flex",
    flexDirection: "column",
  },
  topbar: {
    background: color.surface.card,
    borderBottom: `1px solid ${color.border.default}`,
    padding: "0 20px",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  main: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: space.lg },
  card: {
    maxWidth: 440,
    width: "100%",
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.lg,
    padding: space.xl,
    textAlign: "center",
  },
  title: {
    margin: `0 0 ${space.sm}px`,
    fontSize: font.size.display,
    fontWeight: font.weight.bold,
    lineHeight: font.lineHeight.tight,
    color: color.surface.errorInk,
  },
  body: {
    margin: `0 0 ${space.lg}px`,
    fontSize: font.size.body,
    lineHeight: font.lineHeight.normal,
    color: color.text.secondary,
  },
  cta: {
    background: color.accent.primary,
    color: "#fff",
    border: "none",
    borderRadius: radius.md,
    padding: "10px 20px",
    fontSize: font.size.body,
    fontWeight: font.weight.semibold,
    fontFamily: "inherit",
    minHeight: 44,
  },
};
